/** Partial address record from API space or legacy payloads. */
export interface PlaceAddressRecord {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  countryCode?: string | null;
  googleMapsUrl?: string | null;
  location?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

/** Read-only space address fields on tutor listing / host tutor responses. */
export interface TutorSpaceAddressSource {
  spaceAddressLine1?: string | null;
  spaceAddressLine2?: string | null;
  spaceCity?: string | null;
  spaceState?: string | null;
  spaceZipCode?: string | null;
  spaceCountryCode?: string | null;
  spaceGoogleMapsUrl?: string | null;
  spaceLatitude?: number | null;
  spaceLongitude?: number | null;
}

/** Write-only space address fields for PUT /v1/tutor/listing. */
export interface TutorSpaceAddressWritePayload {
  spaceAddressLine1?: string | null;
  spaceAddressLine2?: string | null;
  spaceCity?: string | null;
  spaceState?: string | null;
  spaceZipCode?: string | null;
  spaceCountryCode?: string | null;
  spaceGoogleMapsUrl?: string | null;
  spaceLatitude?: string | null;
  spaceLongitude?: string | null;
}

type SpaceAddressSource = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  countryCode: string;
  googleMapsUrl: string;
  latitude: number;
  longitude: number;
  location?: string | String | null;
};

export function placeAddressRecordFromSpace(space: SpaceAddressSource): PlaceAddressRecord {
  return {
    addressLine1: space.addressLine1,
    addressLine2: space.addressLine2,
    city: space.city,
    state: space.state,
    zipCode: space.zipCode,
    countryCode: space.countryCode,
    googleMapsUrl: space.googleMapsUrl,
    latitude: space.latitude,
    longitude: space.longitude,
    location: space.location != null ? String(space.location) : null,
  };
}

export function placeAddressRecordFromTutorSpaceAddress(
  source?: TutorSpaceAddressSource | null
): PlaceAddressRecord {
  if (!source) return {};
  return {
    addressLine1: source.spaceAddressLine1,
    addressLine2: source.spaceAddressLine2,
    city: source.spaceCity,
    state: source.spaceState,
    zipCode: source.spaceZipCode,
    countryCode: source.spaceCountryCode,
    googleMapsUrl: source.spaceGoogleMapsUrl,
    latitude: source.spaceLatitude,
    longitude: source.spaceLongitude,
  };
}

export function tutorSpaceAddressWriteFromDraft(draft: PlaceAddressDraft): TutorSpaceAddressWritePayload {
  return {
    spaceAddressLine1: draft.addressLine1.trim() || null,
    spaceAddressLine2: draft.addressLine2.trim() || null,
    spaceCity: draft.city.trim() || null,
    spaceState: draft.state.trim() || null,
    spaceZipCode: draft.zipCode.trim() || null,
    spaceCountryCode: draft.countryCode.trim() || null,
    spaceGoogleMapsUrl: draft.googleMapsUrl.trim() || null,
    spaceLatitude: draft.latitude != null ? String(draft.latitude) : null,
    spaceLongitude: draft.longitude != null ? String(draft.longitude) : null,
  };
}

export interface PlaceAddressValidationFieldErrors {
  placeLocation?: string;
  addressLine1?: string;
}

/** Address fields filled from Google Places, aligned with host space forms. */
export interface PlaceAddressDraft {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  countryCode: string;
  googleMapsUrl: string;
  location: string;
  latitude: number | null;
  longitude: number | null;
}

export function createEmptyPlaceAddressDraft(): PlaceAddressDraft {
  return {
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    countryCode: '',
    googleMapsUrl: '',
    location: '',
    latitude: null,
    longitude: null,
  };
}

export function hasPlaceLocationSelected(draft: PlaceAddressDraft): boolean {
  return (
    draft.googleMapsUrl.trim().length > 0 &&
    draft.latitude != null &&
    draft.longitude != null &&
    Number.isFinite(draft.latitude) &&
    Number.isFinite(draft.longitude)
  );
}

export function applyGooglePlaceToPlaceAddressDraft(
  place: google.maps.places.PlaceResult,
  current: PlaceAddressDraft
): PlaceAddressDraft | null {
  if (!place.geometry?.location) {
    return null;
  }

  const lat = place.geometry.location.lat();
  const lng = place.geometry.location.lng();
  const next: PlaceAddressDraft = { ...current };

  next.googleMapsUrl = place.url || '';
  next.latitude = lat;
  next.longitude = lng;
  next.location = (place.name || place.formatted_address || '').trim();

  let streetNumber = '';
  let route = '';

  place.address_components?.forEach((component) => {
    const types = component.types;
    if (types.includes('locality')) {
      next.city = component.long_name;
    } else if (types.includes('administrative_area_level_1')) {
      next.state = component.long_name;
    } else if (types.includes('postal_code')) {
      next.zipCode = component.long_name;
    } else if (types.includes('street_number')) {
      streetNumber = component.long_name;
    } else if (types.includes('route')) {
      route = component.long_name;
    } else if (types.includes('country')) {
      next.countryCode = component.short_name;
    } else if (types.includes('sublocality') && !next.addressLine2) {
      next.addressLine2 = component.long_name;
    }
  });

  next.addressLine1 = [route, streetNumber].filter(Boolean).join(' ');

  return next;
}

export function placeAddressDraftFromRecord(address?: PlaceAddressRecord | null): PlaceAddressDraft {
  const base = createEmptyPlaceAddressDraft();
  if (!address) return base;
  return {
    addressLine1: address.addressLine1?.trim() ?? '',
    addressLine2: address.addressLine2?.trim() ?? '',
    city: address.city?.trim() ?? '',
    state: address.state?.trim() ?? '',
    zipCode: address.zipCode?.trim() ?? '',
    countryCode: address.countryCode?.trim() ?? '',
    googleMapsUrl: address.googleMapsUrl?.trim() ?? '',
    location: address.location != null ? String(address.location).trim() : '',
    latitude: address.latitude ?? null,
    longitude: address.longitude ?? null,
  };
}

export function getPlaceAddressFieldErrors(
  draft: PlaceAddressDraft,
  translate: (key: string, fallback: string) => string
): PlaceAddressValidationFieldErrors {
  const errors: PlaceAddressValidationFieldErrors = {};
  if (!hasPlaceLocationSelected(draft)) {
    errors.placeLocation = translate(
      'space.placeLocationRequired',
      'Please select a location from Google Maps'
    );
  }
  if (!draft.addressLine1.trim()) {
    errors.addressLine1 = translate('space.addressLine1Required', 'Address Line 1 is required');
  }
  return errors;
}

export function isPlaceAddressCompleteForOffline(draft: PlaceAddressDraft): boolean {
  return Object.keys(getPlaceAddressFieldErrors(draft, (_k, fb) => fb)).length === 0;
}

export function validateOfflineSessionPlaceAddress(
  addressDraft: PlaceAddressDraft,
  translate: (key: string, fallback: string) => string
): PlaceAddressValidationFieldErrors | null {
  const errors = getPlaceAddressFieldErrors(addressDraft, translate);
  return Object.keys(errors).length > 0 ? errors : null;
}

export function formatPlaceAddressSummary(
  address?: PlaceAddressRecord | null,
  fallback?: string | null
): string | null {
  if (!address) return fallback?.trim() || null;
  const line1 = [address.addressLine1, address.addressLine2].filter((part) => part?.trim()).join(', ');
  const line2 = [address.city, address.state, address.zipCode].filter((part) => part?.trim()).join(', ');
  const parts = [line1, line2, address.countryCode?.trim()].filter((part) => part && part.length > 0);
  if (parts.length === 0) {
    const location = address.location != null ? String(address.location).trim() : '';
    return location || fallback?.trim() || null;
  }
  return parts.join(' • ');
}
